/* =========================================================
   MVHR / MEV Kit Builder (Server-Side SKU + Rules)
   Browser now sends inputs/options to the Worker and receives:
   - calculated results
   - hydrated kit lines
   - totals
========================================================= */

const QUICK_QUOTE_PROXY = "/apps/quick-quote";
const CRM_SOURCE_TAG = "mvhr-mev-kitbuilder-preview";

let TURNSTILE_TOKEN = "";
window.onTurnstileSuccess = function(token) {
  TURNSTILE_TOKEN = String(token || "").trim();
};
window.onTurnstileExpired = function() {
  TURNSTILE_TOKEN = "";
};

let systemType = "mvhr";
let lastCalc = null;
let lastKitLines = [];
let calcRunId = 0;

function $(id) { return document.getElementById(id) || null; }

function escapeHtml(s) {
  return String(s || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function round1(x) {
  return Math.round((Number(x || 0) + Number.EPSILON) * 10) / 10;
}

function fmtMoney(amount, currency = "GBP") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function csvEscape(val) {
  const s = String(val ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toBase64Utf8(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

async function postQuoteToProxy(payload) {
  const res = await fetch(QUICK_QUOTE_PROXY, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify(payload)
  });

  const text = await res.text();
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const looksLikeJson = ct.includes("application/json") || text.trim().startsWith("{");

  if (!looksLikeJson) {
    throw new Error(
      `Proxy returned non-JSON (${res.status}). ` +
      `First 120 chars: ${text.trim().slice(0, 120)}`
    );
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON from proxy (${res.status}). First 120 chars: ${text.trim().slice(0, 120)}`);
  }

  if (!res.ok) {
    throw new Error((data && (data.error || data.message)) || `HTTP ${res.status}`);
  }

  if (data && data.ok === false) {
    throw new Error(data.error || "Worker returned ok:false");
  }

  return data;
}



function getConfirmBtn() {
  return document.getElementById("addKitBtn")
    || document.querySelector('#kitPreview button[onclick="addKitToCart()"]');
}

function ensureViewKitPreviewButton() {
  const addBtn = getConfirmBtn();
  const kitSection = document.getElementById("kitPreview");
  if (!addBtn || !kitSection) return;
  if (document.getElementById("viewKitPreviewBtn")) return;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.id = "viewKitPreviewBtn";
  btn.className = addBtn.className || "";
  btn.textContent = "View kit preview";
  addBtn.insertAdjacentElement("afterend", btn);
  btn.addEventListener("click", () => {
    kitSection.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function renderError(msg) {
  const result = $("result");
  if (result) result.innerHTML = `<div class="result-wrap"><strong>${escapeHtml(msg)}</strong></div>`;
  const preview = $("kitPreview");
  if (preview) preview.style.display = "none";
  const costEl = $("kitCost");
  if (costEl) costEl.textContent = "Kit total cost: —";
  const confirmBtn = getConfirmBtn();
  if (confirmBtn) confirmBtn.disabled = true;
}

function renderZeroSummary() {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          0 L/s
          <span class="result-hero__sub">(0 m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: —</div>
      </div>
      <div class="rates-grid">
        <div class="rate-card"><div class="rate-card__label">Floor area rate</div><div class="rate-card__value">0 <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Bedrooms rate</div><div class="rate-card__value">0 <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Wet rooms rate</div><div class="rate-card__value">0 <span class="unit">L/s</span></div></div>
      </div>
    </div>
  `;
}

function renderMvhrSummary(results) {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          ${round1(results.governingLs)} L/s
          <span class="result-hero__sub">(${Number(results.governingM3h || 0).toFixed(0)} m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: <strong>${escapeHtml(results.governingLabel || "—")}</strong></div>
      </div>
      <div class="rates-grid">
        <div class="rate-card"><div class="rate-card__label">Floor area rate</div><div class="rate-card__value">${round1(results.floorAreaRateLs)} <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Bedrooms rate</div><div class="rate-card__value">${round1(results.bedroomsRateLs)} <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Wet rooms rate</div><div class="rate-card__value">${round1(results.wetRoomsRateLs)} <span class="unit">L/s</span></div></div>
      </div>
    </div>
  `;
}

function renderMevSummary(results) {
  const result = $("result");
  if (!result) return;

  result.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero">
        <div class="result-hero__label">Required ventilation rate</div>
        <div class="result-hero__value">
          ${round1(results.governingLs)} L/s
          <span class="result-hero__sub">(${Number(results.governingM3h || 0).toFixed(0)} m³/h)</span>
        </div>
        <div class="result-hero__meta">Governing: <strong>MEV airflow target</strong></div>
      </div>
      <div class="rates-grid">
        <div class="rate-card"><div class="rate-card__label">Floor area rate</div><div class="rate-card__value">— <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Bedrooms rate</div><div class="rate-card__value">— <span class="unit">L/s</span></div></div>
        <div class="rate-card"><div class="rate-card__label">Wet rooms rate</div><div class="rate-card__value">— <span class="unit">L/s</span></div></div>
      </div>
    </div>
  `;
}

function resetOutputs() {
  const choiceEl = $("mvhrChoice");
  const previewEl = $("kitPreview");
  const costEl = $("kitCost");
  const bodyEl = $("kitPreviewBody");
  const headEl = $("kitPreviewHead");

  renderZeroSummary();
  if (choiceEl) choiceEl.innerHTML = "";
  if (previewEl) previewEl.style.display = "none";
  if (bodyEl) bodyEl.innerHTML = "";
  if (headEl) headEl.innerHTML = "";
  if (costEl) costEl.textContent = "Kit total cost: —";

  lastCalc = null;
  lastKitLines = [];

  const confirmBtn = getConfirmBtn();
  if (confirmBtn) confirmBtn.disabled = true;
}

function getMvhrInputsHtml() {
  return `
    <label>Total Project Floor Area (m²)</label>
    <input type="number" id="floorArea" placeholder="e.g. 95">

    <label>No. of Bedrooms</label>
    <input type="number" id="bedrooms" placeholder="e.g. 2">

    <label>No. of Living Rooms</label>
    <input type="number" id="livingRooms" placeholder="e.g. 1">

    <label>Other Habitable Rooms</label>
    <input type="number" id="otherRooms" placeholder="e.g. 1">

    <label>No. of Kitchens</label>
    <input type="number" id="kitchens" placeholder="e.g. 1">

    <label>No. of Bathrooms</label>
    <input type="number" id="bathrooms" placeholder="e.g. 2">

    <label>No. of Utility Rooms</label>
    <input type="number" id="utility" placeholder="e.g. 1">

    <label>No. of Water Closets</label>
    <input type="number" id="wcs" placeholder="e.g. 1">

    <button id="airflowCalcBtn" type="button">Calculate MVHR &amp; Build</button>
  `;
}

function getMevInputsHtml() {
  return `
    <label>Total Project Floor Area (m²)</label>
    <input type="number" id="floorArea" placeholder="e.g. 95">

    <label>No. of Kitchens</label>
    <input type="number" id="kitchens" placeholder="e.g. 1">

    <label>No. of Bathrooms</label>
    <input type="number" id="bathrooms" placeholder="e.g. 2">

    <label>No. of Utility Rooms</label>
    <input type="number" id="utility" placeholder="e.g. 1">

    <label>No. of Water Closets</label>
    <input type="number" id="wcs" placeholder="e.g. 1">

    <button id="airflowCalcBtn" type="button">Calculate MEV &amp; Build</button>
  `;
}

function collectInputs(mode) {
  const floorArea = Number($("floorArea")?.value || 0);
  if (mode === "mvhr") {
    return {
      floorArea,
      bedrooms: Number($("bedrooms")?.value || 0),
      livingRooms: Number($("livingRooms")?.value || 0),
      otherRooms: Number($("otherRooms")?.value || 0),
      kitchens: Number($("kitchens")?.value || 0),
      bathrooms: Number($("bathrooms")?.value || 0),
      utility: Number($("utility")?.value || 0),
      wcs: Number($("wcs")?.value || 0)
    };
  }
  return {
    floorArea,
    kitchens: Number($("kitchens")?.value || 0),
    bathrooms: Number($("bathrooms")?.value || 0),
    utility: Number($("utility")?.value || 0),
    wcs: Number($("wcs")?.value || 0)
  };
}

function collectOptions(mode) {
  if (mode === "mvhr") {
    return {
      selectedUnitSku: String($("mvhrSelect")?.value || ""),
      upgradedValves: String($("upgradedValves")?.value || "no"),
      concreteFloors: String($("concreteFloors")?.value || "no")
    };
  }
  return {
    selectedUnitSku: String($("mevSelect")?.value || ""),
    upgradedValves: String($("upgradedValves")?.value || "no"),
    installRegion: String($("installRegion")?.value || "uk")
  };
}

function renderChoiceUI(mode, choices, options) {
  const el = $("mvhrChoice");
  if (!el) return;

  const units = Array.isArray(choices?.units) ? choices.units : [];
  if (mode === "mvhr") {
    el.innerHTML = `
      <label style="font-weight:600; display:block; margin:10px 0 6px;">Choose MVHR Unit</label>
      <select id="mvhrSelect" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px;">
        ${units.map(u => `<option value="${escapeHtml(u.sku)}">${escapeHtml(u.name || u.sku)}</option>`).join("")}
      </select>

      <label style="margin-top:12px;">Upgraded air valves?</label>
      <select id="upgradedValves" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px; margin-bottom:12px;">
        <option value="no">No</option>
        <option value="yes">Yes</option>
      </select>

      <label>1st floor concrete floors?</label>
      <select id="concreteFloors" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px; margin-bottom:12px;">
        <option value="no">No</option>
        <option value="yes">Yes</option>
      </select>
    `;

    if ($("mvhrSelect")) $("mvhrSelect").value = String(options?.selectedUnitSku || units[0]?.sku || "");
    if ($("upgradedValves")) $("upgradedValves").value = String(options?.upgradedValves || "no");
    if ($("concreteFloors")) $("concreteFloors").value = String(options?.concreteFloors || "no");
    $("mvhrSelect")?.addEventListener("change", () => buildFromServer("mvhr"));
    $("upgradedValves")?.addEventListener("change", () => buildFromServer("mvhr"));
    $("concreteFloors")?.addEventListener("change", () => buildFromServer("mvhr"));
    return;
  }

  el.innerHTML = `
    <label style="font-weight:600; display:block; margin:10px 0 6px;">Choose MEV Unit</label>
    <select id="mevSelect" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px;">
      ${units.map(u => `<option value="${escapeHtml(u.sku)}">${escapeHtml(u.name || u.sku)}</option>`).join("")}
    </select>

    <label style="margin-top:12px;">Upgraded air valves?</label>
    <select id="upgradedValves" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px; margin-bottom:12px;">
      <option value="no">No</option>
      <option value="yes">Yes</option>
    </select>

    <label>Install location</label>
    <select id="installRegion" style="width:100%; padding:12px; height:48px; border-radius:6px; border:1px solid #ccc; font-size:16px; margin-bottom:12px;">
      <option value="uk">UK</option>
      <option value="ie">Ireland</option>
    </select>
  `;

  if ($("mevSelect")) $("mevSelect").value = String(options?.selectedUnitSku || units[0]?.sku || "");
  if ($("upgradedValves")) $("upgradedValves").value = String(options?.upgradedValves || "no");
  if ($("installRegion")) $("installRegion").value = String(options?.installRegion || "uk");
  $("mevSelect")?.addEventListener("change", () => buildFromServer("mev"));
  $("upgradedValves")?.addEventListener("change", () => buildFromServer("mev"));
  $("installRegion")?.addEventListener("change", () => buildFromServer("mev"));
}

function renderKit(lines, totals) {
  const preview = $("kitPreview");
  const body = $("kitPreviewBody");
  const head = $("kitPreviewHead");
  const costEl = $("kitCost");
  const confirmBtn = getConfirmBtn();

  if (preview) preview.style.display = "block";
  ensureViewKitPreviewButton();

  if (head) {
    head.innerHTML = `
      <tr>
        <th class="img-col"></th>
        <th align="left">Item</th>
        <th align="left">SKU</th>
        <th class="qty-col" style="text-align:right;">Qty</th>
        <th align="right">Total</th>
      </tr>
    `;
  }

  if (body) {
    body.innerHTML = `
      ${(lines || []).map(line => {
        const img = line.image
          ? `<img class="thumb" src="${escapeHtml(line.image)}" alt="" loading="lazy">`
          : `<div class="thumb" aria-hidden="true"></div>`;

        const lineTotal = (Number(line.price) || 0) * (Number(line.qty) || 0);
        return `
          <tr>
            <td class="cImg">${img}</td>
            <td>
              <div style="font-weight:700">${escapeHtml(line.title || line.sku)}</div>
              ${!line.variantId ? `<div class="hint" style="color:#b00020">SKU not found in Shopify</div>` : ``}
              ${line.available === false ? `<div class="hint" style="color:#b00020">Out of stock</div>` : ``}
              ${line.available === null ? `<div class="hint" style="color:#666">Stock unknown</div>` : ``}
            </td>
            <td class="cSku">${escapeHtml(line.sku)}</td>
            <td class="cQty" style="text-align:right">${Number(line.qty || 0)}</td>
            <td class="cPrice" style="text-align:right">${fmtMoney(lineTotal, line.currency || totals?.currency || "GBP")}</td>
          </tr>
        `;
      }).join("")}
    `;
  }

  const currency = totals?.currency || lines?.find(x => x.currency)?.currency || "GBP";
  const exVat = Number(totals?.exVat || 0);
  const incVat = Number(totals?.incVat || (exVat * 1.2));
  if (costEl) {
    costEl.innerHTML = `
      <div class="kit-cost-lines">
        <div class="kit-cost-ex">
          Kit total cost (ex VAT):
          <span class="kit-total-amount">${fmtMoney(exVat, currency)}</span>
        </div>
        <div class="kit-cost-inc">
          inc VAT:
          <strong>${fmtMoney(incVat, currency)}</strong>
        </div>
      </div>
    `;
  }

  const anyMissing = (lines || []).some(x => !x.variantId);
  if (confirmBtn) confirmBtn.disabled = anyMissing;
}

async function buildFromServer(mode) {
  const runId = ++calcRunId;

  const body = $("kitPreviewBody");
  const preview = $("kitPreview");
  const costEl = $("kitCost");
  const confirmBtn = getConfirmBtn();

  if (preview) preview.style.display = "block";
  if (body) body.innerHTML = `<tr><td colspan="5" class="empty">Building kit…</td></tr>`;
  if (costEl) costEl.textContent = "Kit total cost: Calculating…";
  if (confirmBtn) confirmBtn.disabled = true;

  try {
    const inputs = collectInputs(mode);
    if (!inputs.floorArea || inputs.floorArea <= 0) {
      renderError("Please enter a valid floor area");
      return;
    }

    if (mode === "mev") {
      const extractRooms = Number(inputs.kitchens || 0) + Number(inputs.bathrooms || 0) + Number(inputs.utility || 0) + Number(inputs.wcs || 0);
      if (extractRooms <= 0) {
        renderError("Please enter at least one extract room (Kitchen, bathroom, utility or WC).");
        return;
      }
    }

    const options = collectOptions(mode);
    const resp = await postQuoteToProxy({
      action: "build_kit",
      systemType: mode,
      inputs,
      options
    });

    if (runId !== calcRunId) return;

    lastCalc = {
      type: mode,
      inputs: resp.inputs || inputs,
      options: resp.options || options,
      results: resp.results || {},
      totals: resp?.kit?.totals || {}
    };
    lastKitLines = Array.isArray(resp?.kit?.lines) ? resp.kit.lines : [];

    if (mode === "mvhr") renderMvhrSummary(lastCalc.results);
    else renderMevSummary(lastCalc.results);

    renderChoiceUI(mode, resp.choices || {}, resp.options || options);
    renderKit(lastKitLines, resp?.kit?.totals || {});
  } catch (err) {
    console.error(err);
    renderError(err?.message || "Something went wrong.");
  }
}

function renderCalculator(mode = "mvhr") {
  const mount = $("calculatorMount");
  if (!mount) return;

  resetOutputs();
  mount.innerHTML = mode === "mev" ? getMevInputsHtml() : getMvhrInputsHtml();
  $("airflowCalcBtn")?.addEventListener("click", () => {
    buildFromServer(mode);
  });
}

function buildKitToText() {
  const rows = (lastKitLines || []).map(x => {
    const sku = String(x.sku || "").trim();
    const qty = Number(x.qty || 0);
    return `${sku}\t${qty}`;
  });
  return ["SKU\tQTY", ...rows].join("\n");
}

async function addKitToCart() {
  try {
    if (!lastKitLines?.length) {
      alert("Please calculate a kit first.");
      return;
    }

    const missing = lastKitLines.find(x => !x.variantId);
    if (missing) {
      alert(`Cannot add to cart: SKU not found (${missing.sku})`);
      return;
    }

    const outOfStock = lastKitLines.filter(x => x.available === false);
    if (outOfStock.length) {
      const names = outOfStock.map(x => x.title).join(", ");
      alert(`Sorry — one or more items in this kit are currently out of stock:\n\n${names}`);
      return;
    }

    const items = lastKitLines.map(x => ({
      id: Number(x.variantId),
      quantity: Number(x.qty)
    }));

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      let msg = "Could not add kit to cart.";
      try {
        const data = await res.json();
        if (data?.description) msg = data.description;
      } catch {}
      throw new Error(msg);
    }

    window.location.href = "/cart";
  } catch (err) {
    console.error(err);
    alert(err?.message || "Could not add kit to cart.");
  }
}

window.addKitToCart = addKitToCart;

function wireQuoteSender() {
  const emailEl = document.getElementById("qqEmail") || document.getElementById("quoteEmail");
  const sendBtn = document.getElementById("qqSendBtn") || document.getElementById("sendQuoteBtn");
  if (!emailEl || !sendBtn) return;
  if (sendBtn.dataset.bound === "1") return;
  sendBtn.dataset.bound = "1";

  sendBtn.addEventListener("click", async () => {
    const oldText = sendBtn.textContent;
    try {
      const email = String(emailEl.value || "").trim();
      if (!isValidEmail(email)) {
        alert("Please enter a valid email address.");
        emailEl.focus();
        return;
      }

      const nameEl = document.getElementById("qqName") || document.getElementById("quoteName");
      const name = String(nameEl?.value || "").trim();
      if (!name) {
        alert("Please enter your name.");
        nameEl?.focus();
        return;
      }

      if (!TURNSTILE_TOKEN) {
        alert("Please complete the human check before sending your quote.");
        return;
      }

      if (!lastCalc || !lastKitLines.length) {
        alert("Please calculate and build a kit first.");
        return;
      }

      sendBtn.disabled = true;
      sendBtn.textContent = "Sending…";

      const currency = lastCalc?.totals?.currency || lastKitLines.find(x => x.currency)?.currency || "GBP";
      const exVat = Number(lastCalc?.totals?.exVat || lastKitLines.reduce((s, x) => s + (Number(x.price) || 0) * (Number(x.qty) || 0), 0));
      const incVat = Number(lastCalc?.totals?.incVat || (exVat * 1.2));
      const type = lastCalc.type || "mvhr";

      const csvRows = [
        ["Title", "SKU", "Qty", "Unit Price", "Currency", "Line Total"],
        ...lastKitLines.map(x => {
          const unit = Number(x.price || 0);
          const qty = Number(x.qty || 0);
          return [x.title || "", x.sku || "", qty, unit, x.currency || currency, unit * qty];
        })
      ];
      const csvText = csvRows.map(r => r.map(csvEscape).join(",")).join("\n");
      const ts = new Date().toISOString().slice(0, 10);

      const payload = {
        action: "submit_quote",
        turnstile_token: TURNSTILE_TOKEN,
        email,
        name,
        customer: { email, name },
        kitto_text: buildKitToText(),
        kit: {
          totals: {
            currency,
            exVat: Number(exVat.toFixed(2)),
            incVat: Number(incVat.toFixed(2)),
            exVatText: fmtMoney(exVat, currency),
            incVatText: fmtMoney(incVat, currency)
          },
          lines: (lastKitLines || []).map(x => ({
            sku: x.sku,
            qty: x.qty,
            unitPrice: x.price,
            currency: x.currency || currency,
            title: x.title,
            image: x.image,
            variantId: x.variantId || null
          }))
        },
        quote: {
          items: (lastKitLines || []).map(x => {
            const qty = Number(x.qty || 0);
            const unit = Number(x.price || 0);
            const lineTotal = qty * unit;
            return {
              sku: x.sku,
              quantity: qty,
              qty,
              title: x.title,
              unitPrice: unit,
              price: unit,
              unit_price: unit,
              lineTotal,
              line_total: lineTotal,
              currency: x.currency || currency
            };
          }).filter(i => i.sku && i.quantity > 0)
        },
        systemType: type,
        inputs: lastCalc.inputs || {},
        results: lastCalc.results || {},
        totals: {
          currency,
          exVat: Number(exVat.toFixed(2)),
          incVat: Number(incVat.toFixed(2)),
          exVatText: fmtMoney(exVat, currency),
          incVatText: fmtMoney(incVat, currency)
        },
        meta: {
          source: CRM_SOURCE_TAG,
          page: location.href,
          systemType: type
        },
        attachments: [
          {
            filename: `materials-${type}-${ts}.csv`,
            contentType: "text/csv",
            base64: toBase64Utf8(csvText)
          }
        ]
      };

      const resp = await postQuoteToProxy(payload);
      if (resp?.ok) {
        sendBtn.textContent = "Sent ✅";
        setTimeout(() => window.location.reload(), 600);
        return;
      }

      throw new Error(resp?.error || "Could not send quote.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Could not send quote.");
      sendBtn.disabled = false;
      sendBtn.textContent = oldText || "Send quote";
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  const topSel = $("systemTypeTop");
  if (topSel) {
    topSel.addEventListener("change", () => {
      systemType = topSel.value || "mvhr";
      renderCalculator(systemType);
    });
  }

  systemType = topSel?.value || "mvhr";
  renderCalculator(systemType);

  const addBtn = document.getElementById("addKitBtn");
  if (addBtn && addBtn.dataset.bound !== "1") {
    addBtn.dataset.bound = "1";
    addBtn.addEventListener("click", addKitToCart);
  }

  wireQuoteSender();
});
